import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LeftOutlined, PlusOutlined, ImportOutlined, ApartmentOutlined } from '@ant-design/icons';
import { addField, removeField } from '@/store/reducers/headerContent';
import { Input, Tooltip, Form, Button } from 'antd';
import { toast } from 'react-toastify';
import { import_recipe_json } from '../../utils/api';

import { useLocation } from 'react-router-dom';
import DAGFlowCopy from '@/pages/dashboard/ArchiMateFlow';
import { accessControlUtils } from '@/components/AccessControlUtils';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';

// New imports for refactored architecture
import { useProductData } from '@/hooks/recipe';

/**
 * Portals its children into a DOM node (by id) that lives in the ViewRecipe
 * MainCard header, so RecipeConfig's toolbar buttons render on the card-header
 * line. Resolves the target after mount (the header renders before this child).
 */
const HeaderSlot = ({ targetId, children }) => {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    setTarget(document.getElementById(targetId));
  });
  if (!target) return null;
  return createPortal(children, target);
};

function RecipeConfig({
  selectedButton,
  support_mbmr = false,
  recipe_id,
  selectedRecipeName,
  setSelectedRecipeName,
  product_id,
  searchvalue,
  setSearchvalue,
  grid_list,
  set_product_id,
  set_recipe_id,
  set_grid_list,
  pendingStatus,
  setDependencyGraphOpen,
}) {
  const { isDark } = useTheme();
  const location = useLocation();
  const accessData = accessControlUtils('View MBMR') || {
    create_access: false,
    read_access: false,
    update_access: false,
    delete_access: false,
  };
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedModule = useSelector((state) => state.moduleView);

  // Use Product Data hook instead of manual API calls
  const { products: product_list } = useProductData(selectedButton, selectedModule, {
    autoFetch: true,
    enableSearch: true,
  });

  // Local state - only what's actually used
  const [flow_data, set_flow_data] = useState();
  const [importLoading, setImportLoading] = useState(false);
  const importFileRef = useRef(null);

  const handleImportRecipe = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const jsonData = JSON.parse(evt.target.result);
        await import_recipe_json(
          // Support MBMRs are productless — send the flag and omit product_id so
          // the imported recipe is created without a product and lands in the
          // /support-mbmr list. Otherwise import into the selected product.
          support_mbmr
            ? { recipe_data: jsonData, support_mbmr: true }
            : { recipe_data: jsonData, product_id: product_id },
          (res) => {
            if (res?.data?.status === 'success') {
              toast.success(`Recipe "${res.data.data?.new_recipe_name}" imported successfully`);
              window.location.reload();
            } else {
              toast.error(res?.data?.message || 'Import failed');
            }
            setImportLoading(false);
          },
          (err) => {
            toast.error(err?.response?.data?.message || 'Import failed');
            setImportLoading(false);
          }
        );
      } catch (parseErr) {
        toast.error('Invalid JSON file');
        setImportLoading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleClick = (button) => {
    if (button !== 'list' && button !== 'grid') {
      set_flow_data();
    } else {
      set_grid_list(button);
    }
  };

  const clearSearchValue = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setSearchvalue();
  };

  // Set flow data when product/recipe changes
  useEffect(() => {
    if (!product_id) {
      set_flow_data();
    } else if (product_id || recipe_id) {
      set_flow_data();
      setTimeout(() => {
        set_flow_data({ recipe_id: recipe_id });
      }, 0.5);
    }
    clearSearchValue();
  }, [product_id, recipe_id]);

  useEffect(() => {
    // set product name and recipe name to show in header
    if ((product_list && product_list?.length > 0) || selectedRecipeName) {
      const productName =
        product_list?.find((p) => p.product_id === product_id)?.product_name ||
        'No Product Selected';

      productName && dispatch(addField({ key: 'Product', value: productName || '' }));
      selectedRecipeName && dispatch(addField({ key: 'MBMR', value: selectedRecipeName || '' }));
    }
  }, [product_id, recipe_id, selectedRecipeName]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Product display removed here — the selected product is already
              shown in the main page header, so this was a duplicate. */}

          {selectedRecipeName && (
            <Form.Item
              label={
                <span className="font-bold" style={{ color: isDark ? '#ffffff' : '#204448' }}>
                  {selectedModule === 'prod' ? 'MBMR' : 'MBPR'}
                </span>
              }
              className="mb-0"
            >
              <div
                className="px-3 py-1.5 rounded text-sm min-w-[180px]"
                style={{
                  backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f3f4f6',
                  color: isDark ? '#ffffff' : '#204448',
                }}
              >
                {selectedRecipeName || 'No Recipe Selected'}
              </div>
            </Form.Item>
          )}
        </div>

        {/* Action buttons are portaled to the card header line (right side). */}
        <HeaderSlot targetId="recipe-header-actions">
          {/* Same action buttons on Support MBMR (productless) as the product page. */}
          {(product_id || support_mbmr) && !recipe_id && (
            <>
              {selectedButton === 'test' &&
                (accessData.create_access || accessData.update_access) && (
                  <>
                    <Tooltip title={`Import ${selectedModule === 'prod' ? 'MBMR' : 'MBPR'}`} placement="left">
                      <Button
                        icon={<ImportOutlined />}
                        loading={importLoading}
                        style={{
                          backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                          borderColor: isDark ? '#0F172A' : defaultColors.primary,
                          color: '#fff',
                        }}
                        onClick={() => importFileRef.current?.click()}
                      >
                        Import {selectedModule === 'prod' ? 'MBMR' : 'MBPR'}
                      </Button>
                    </Tooltip>
                    <input
                      type="file"
                      ref={importFileRef}
                      accept=".json"
                      style={{ display: 'none' }}
                      onChange={handleImportRecipe}
                    />
                    {/* Prototype DAG of source_mbmr relationships (packaging Source
                        MBMR / input-based Select Recipe) for this product's recipes.
                        Read-only, so not gated on create/update access like Import/Add. */}
                    {product_id && (
                      <Tooltip title="Recipe Dependencies" placement="left">
                        <Button
                          icon={<ApartmentOutlined />}
                          onClick={() => setDependencyGraphOpen(true)}
                        >
                          Recipe Dependencies
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip
                      title={`Add ${selectedModule === 'prod' ? 'MBMR' : 'MBPR'}`}
                      placement="left"
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{
                          backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                          borderColor: isDark ? '#0F172A' : defaultColors.primary,
                        }}
                        onClick={() => {
                          navigate('/recipeMetadata', {
                            // Support MBMR: create a productless recipe (no product_id).
                            state: support_mbmr
                              ? { support_mbmr: true }
                              : { product_id: product_id },
                          });
                        }}
                      >
                        Add
                      </Button>
                    </Tooltip>
                  </>
                )}
              {/* Support MBMR: no List/Grid toggle. */}
              {!support_mbmr && (
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <button
                  onClick={() => handleClick('list')}
                  className={`px-4 text-sm font-medium border
                  ${
                    grid_list === 'list'
                      ? 'text-white border-opacity-50'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'
                  }
                  rounded-l-md hover:opacity-90 transition`}
                  style={{
                    height: 36,
                    backgroundColor: grid_list === 'list' ? (isDark ? '#0F172A' : defaultColors.primary) : undefined,
                    borderColor: grid_list === 'list' ? (isDark ? '#0F172A' : defaultColors.primary) : undefined,
                  }}
                >
                  List
                </button>
                <button
                  onClick={() => handleClick('grid')}
                  className={`px-3 text-sm font-medium border
                      ${
                        grid_list === 'grid'
                          ? 'text-white border-opacity-50'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'
                      }
                      rounded-r-md hover:opacity-90 transition`}
                  style={{
                    height: 36,
                    backgroundColor: grid_list === 'grid' ? (isDark ? '#0F172A' : defaultColors.primary) : undefined,
                    borderColor: grid_list === 'grid' ? (isDark ? '#0F172A' : defaultColors.primary) : undefined,
                  }}
                >
                  Grid
                </button>
              </div>
              )}
            </>
          )}
        </HeaderSlot>

        {/* Search is in the ViewRecipe MainCard header; Back goes AFTER it. */}
        <HeaderSlot targetId="recipe-header-back">
          {(product_id || support_mbmr) && (
            <Tooltip title="Back" placement="bottom">
              <Button
                type="primary"
                icon={<LeftOutlined />}
                style={{
                  backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                  borderColor: isDark ? '#0F172A' : defaultColors.primary,
                }}
                onClick={() => {
                  if (recipe_id) {
                    set_recipe_id();
                    setSelectedRecipeName('');
                    dispatch(removeField('MBMR', ''));
                    clearSearchValue();
                  } else if (support_mbmr) {
                    // Support MBMR has no product picker to return to — go back.
                    navigate(-1);
                  } else {
                    set_product_id();
                    setSelectedRecipeName('');
                    dispatch(removeField('Product', ''));
                    set_grid_list('grid');
                    clearSearchValue();
                  }
                }}
              >
                Back
              </Button>
            </Tooltip>
          )}
        </HeaderSlot>
      </div>

      {flow_data?.recipe_id && (
        <DAGFlowCopy
          data={flow_data}
          set_flow_data={set_flow_data}
          isClickable={pendingStatus[recipe_id] && accessData.update_access ? true : false}
        />
      )}
    </div>
  );
}

export default RecipeConfig;
